import { create } from 'zustand';
import { DashboardState, Room, Booking, Order, BanquetHall, BanquetBooking, BanquetOrder } from '../types';
import { apiService } from '../services/api';
import { format, startOfWeek, addDays, isAfter, isBefore, isSameDay } from 'date-fns';

export const useDashboardStore = create<DashboardState>((set, get) => ({
  rooms: [],
  allBookings: [],
  calendarDates: [],
  selectedDate: new Date(),
  isLoading: false,
  error: undefined,
  selectedRoomId: undefined,
  roomOrders: [],
  roomBookings: [],
  activeBooking: undefined,
  isLoadingRoomDetails: false,
  
  // Banquet Management State
  banquetHalls: [],
  allBanquetBookings: [],
  selectedBanquetHallId: undefined,
  banquetOrders: [],
  banquetHallBookings: [],
  activeBanquetBooking: undefined,
  isLoadingBanquetDetails: false,

  initializeCalendar: () => {
    const now = new Date();
    const startOfCurrentWeek = startOfWeek(now, { weekStartsOn: 1 }); // Monday
    
    const dates = [];
    for (let i = 0; i < 7; i++) {
      dates.push(addDays(startOfCurrentWeek, i));
    }
    
    set({ calendarDates: dates });
  },

  loadRooms: async () => {
    set({ isLoading: true, error: undefined });
    
    try {
      const [rooms, bookings] = await Promise.all([
        apiService.getRooms(),
        apiService.getAllBookings(),
      ]);
      
      // Use booking sources directly from API
      const enhancedBookings = bookings.map(booking => ({
        ...booking,
        // API now provides booking_source field, so we use it directly
        bookingSource: booking.bookingSource
      }));
      
      // Enhance rooms with booking data
      const enhancedRooms = rooms.map(room => {
        const roomBooking = enhancedBookings.find(
          booking => booking.roomId === room.roomId && 
                    (booking.bookingStatus === 'checked_in' || 
                     booking.bookingStatus === 'confirmed' ||
                     booking.bookingStatus === 'checked_out')
        );
        
        if (roomBooking) {
          return {
            ...room,
            guestName: roomBooking.guestName,
            bookingStatus: roomBooking.bookingStatus,
            roomType: roomBooking.roomType || room.roomType,
            stayStartDate: roomBooking.actualCheckinDate || roomBooking.originalCheckinDate,
            stayEndDate: roomBooking.currentCheckoutDate,
          };
        }
        
        return room;
      });
      
      set({ 
        rooms: enhancedRooms, 
        allBookings: enhancedBookings, 
        error: undefined 
      });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load data' });
    } finally {
      set({ isLoading: false });
    }
  },

  navigateCalendar: (direction: number) => {
    const { calendarDates } = get();
    const currentStartOfWeek = calendarDates[0];
    const newStartOfWeek = addDays(currentStartOfWeek, direction * 7);
    
    const dates = [];
    for (let i = 0; i < 7; i++) {
      dates.push(addDays(newStartOfWeek, i));
    }
    
    set({ calendarDates: dates });
  },

  selectBooking: async (bookingId: number) => {
    set({ isLoadingRoomDetails: true });
    
    try {
      const { allBookings } = get();
      
      // Find the specific booking
      const selectedBooking = allBookings.find(booking => booking.bookingId === bookingId);
      
      if (!selectedBooking) {
        throw new Error('Booking not found');
      }
      
      
      // Get all bookings for this room
      const roomBookings = allBookings.filter(booking => booking.roomId === selectedBooking.roomId);
      
      // Get orders for this specific booking
      const orders = await apiService.getOrders(bookingId.toString());
      
      set({ 
        selectedRoomId: selectedBooking.roomId,
        roomBookings, 
        activeBooking: selectedBooking, 
        roomOrders: orders 
      });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load booking details' });
    } finally {
      set({ isLoadingRoomDetails: false });
    }
  },

  clearRoomSelection: () => {
    set({ 
      selectedRoomId: undefined, 
      roomOrders: [], 
      roomBookings: [], 
      activeBooking: undefined 
    });
  },

  isRoomOccupiedOnDate: (roomId: string, date: Date) => {
    const { rooms } = get();
    const room = rooms.find(r => r.roomId === roomId);
    
    if (!room || !room.stayStartDate || !room.stayEndDate) {
      return false;
    }
    
    const startDate = new Date(room.stayStartDate);
    const endDate = new Date(room.stayEndDate);
    
    return isAfter(date, addDays(startDate, -1)) && 
           isBefore(date, addDays(endDate, 1));
  },

  getRoomById: (roomId: string) => {
    const { rooms } = get();
    return rooms.find(room => room.roomId === roomId);
  },

  createBooking: async (bookingData: {
    roomId: string;
    guestName: string;
    phone: string;
    email?: string;
    idProof?: string;
    checkinDate: string;
    checkoutDate: string;
    totalAmount: number;
    roomType: string;
    bookingSource: string;
  }) => {
    set({ isLoading: true, error: undefined });
    
    try {
      const newBooking = await apiService.createBooking(bookingData);
      
      // Reload the data to get the updated bookings from the API (including mock data)
      const [rooms, bookings] = await Promise.all([
        apiService.getRooms(),
        apiService.getAllBookings(),
      ]);
      
      // Enhance rooms with booking data
      const enhancedRooms = rooms.map(room => {
        const roomBooking = bookings.find(
          booking => booking.roomId === room.roomId && 
                    (booking.bookingStatus === 'confirmed' || 
                     booking.bookingStatus === 'completed')
        );
        
        if (roomBooking) {
          return {
            ...room,
            guestName: roomBooking.guestName,
            bookingStatus: roomBooking.bookingStatus,
            roomType: roomBooking.roomType || room.roomType,
            stayStartDate: roomBooking.originalCheckinDate,
            stayEndDate: roomBooking.currentCheckoutDate,
          };
        }
        
        return room;
      });
      
      set({ 
        allBookings: bookings,
        rooms: enhancedRooms,
        error: undefined
      });
      
      return newBooking;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to create booking' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  // Banquet Management Actions
  loadBanquetHalls: async () => {
    set({ isLoading: true, error: undefined });
    
    try {
      const [halls, bookings] = await Promise.all([
        apiService.getBanquetHalls(),
        apiService.getAllBanquetBookings(),
      ]);
      
      // Enhance halls with booking data
      const enhancedHalls = halls.map(hall => {
        const hallBooking = bookings.find(
          booking => booking.hallId === hall.hallId && 
                    (booking.bookingStatus === 'confirmed' || 
                     booking.bookingStatus === 'completed')
        );
        
        if (hallBooking) {
          return {
            ...hall,
            guestName: hallBooking.guestName,
            bookingStatus: hallBooking.bookingStatus,
            eventStartDate: hallBooking.eventDate,
            eventEndDate: hallBooking.eventDate, // For single day events
            eventType: hallBooking.eventType,
            status: 'occupied' as const,
          };
        }
        
        return hall;
      });
      
      set({ 
        banquetHalls: enhancedHalls, 
        allBanquetBookings: bookings, 
        error: undefined 
      });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load banquet data' });
    } finally {
      set({ isLoading: false });
    }
  },

  selectBanquetBooking: async (bookingId: number) => {
    set({ isLoadingBanquetDetails: true });
    
    try {
      const { allBanquetBookings } = get();
      
      // Find the specific booking
      const selectedBooking = allBanquetBookings.find(booking => booking.bookingId === bookingId);
      
      if (!selectedBooking) {
        throw new Error('Banquet booking not found');
      }
      
      // Get all bookings for this hall
      const hallBookings = allBanquetBookings.filter(booking => booking.hallId === selectedBooking.hallId);
      
      // Get orders for this specific booking
      const orders = await apiService.getBanquetOrders(bookingId.toString());
      
      set({ 
        selectedBanquetHallId: selectedBooking.hallId,
        banquetHallBookings: hallBookings, 
        activeBanquetBooking: selectedBooking, 
        banquetOrders: orders 
      });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load banquet booking details' });
    } finally {
      set({ isLoadingBanquetDetails: false });
    }
  },

  clearBanquetSelection: () => {
    set({ 
      selectedBanquetHallId: undefined, 
      banquetOrders: [], 
      banquetHallBookings: [], 
      activeBanquetBooking: undefined 
    });
  },

  isBanquetHallOccupiedOnDate: (hallId: string, date: Date) => {
    const { allBanquetBookings } = get();
    
    return allBanquetBookings.some(booking => {
      if (booking.hallId !== hallId) return false;
      const eventDate = new Date(booking.eventDate);
      return isSameDay(eventDate, date) && 
             (booking.bookingStatus === 'confirmed' || booking.bookingStatus === 'completed');
    });
  },

  getBanquetHallById: (hallId: string) => {
    const { banquetHalls } = get();
    return banquetHalls.find(hall => hall.hallId === hallId);
  },

  createBanquetBooking: async (bookingData: {
    hallId: string;
    guestName: string;
    phone: string;
    email?: string;
    eventName: string;
    eventType: BanquetBooking['eventType'];
    eventDate: string;
    startTime: string;
    endTime: string;
    guestCount: number;
    totalAmount: number;
    advanceAmount: number;
    specialRequirements?: string;
    cateringRequired: boolean;
    decorationRequired: boolean;
    audioVisualRequired: boolean;
    bookingSource: string;
  }) => {
    set({ isLoading: true, error: undefined });
    
    try {
      const newBooking = await apiService.createBanquetBooking(bookingData);
      
      // Reload the data to get the updated bookings from the API
      // This ensures we have the latest data including the new booking
      const [halls, bookings] = await Promise.all([
        apiService.getBanquetHalls(),
        apiService.getAllBanquetBookings(),
      ]);
      
      // Enhance halls with booking data
      const enhancedHalls = halls.map(hall => {
        const hallBooking = bookings.find(
          booking => booking.hallId === hall.hallId && 
                    (booking.bookingStatus === 'confirmed' || 
                     booking.bookingStatus === 'completed')
        );
        
        if (hallBooking) {
          return {
            ...hall,
            guestName: hallBooking.guestName,
            bookingStatus: hallBooking.bookingStatus,
            eventStartDate: hallBooking.eventDate,
            eventEndDate: hallBooking.eventDate,
            eventType: hallBooking.eventType,
            status: 'occupied' as const,
          };
        }
        
        return hall;
      });
      
      set({ 
        allBanquetBookings: bookings,
        banquetHalls: enhancedHalls,
        error: undefined 
      });
      
      return newBooking;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to create banquet booking' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
}));

